'use client';

import { useEffect } from 'react';

/**
 * The film and the page below it snap to each other, in both directions.
 *
 * The stage is a full viewport tall, so crossing it by hand is a long drag
 * that spends most of its length showing half a film and half a wall. One
 * nudge down finishes the gesture; one nudge up from just below brings the
 * film back whole.
 *
 * This hijacks the scroll, which is a thing to do carefully rather than a
 * thing to do cleverly. The rules it holds to:
 *
 * - It acts in two narrow bands only: anywhere in the first half of the film
 *   a downward nudge goes to the section, and in the half-film either side of
 *   the boundary an upward nudge goes back to the top. Below that, scrolling
 *   in both directions is untouched — the wall is a long page and nothing
 *   there should ever be grabbed.
 * - Once a snap starts it owns the scroll until it lands: every wheel and
 *   touch event in the meantime is swallowed. Letting them through meant the
 *   browser scrolled natively underneath the animation, and a fast flick came
 *   out as a stutter rather than one move. It releases the moment it arrives,
 *   so nothing is held longer than it has to be.
 * - It releases on `scrollend` where that exists and on a timer where it does
 *   not, so a browser that never reports the end still re-arms.
 * - Anyone who has asked for less motion gets the same jump without the
 *   travel.
 *
 * Keyboard scrolling is deliberately left alone: Page Down and Space already
 * move a predictable amount, and a reader using them has not asked for help.
 */
export const SnapPastHero = ({ targetId }: { targetId: string }) => {
  useEffect(() => {
    /**
     * How far into the film still counts as "on the film", as a fraction of
     * the stage's height.
     *
     * This started at a flat 4px from the top and that was too strict to be
     * reliable. A trackpad opens with a ramp of tiny deltas and a mouse with a
     * detent; either can carry the page tens of pixels before an event large
     * enough to read as intent arrives, by which point a 4px window has closed
     * and the reader is left dragging through the rest of the stage by hand.
     */
    const ON_FILM = 0.5;
    /**
     * How far past the boundary an upward nudge still returns to the film.
     * Kept short on purpose: someone reading the wall and scrolling up should
     * reach the film by scrolling, not be thrown at it.
     */
    const BELOW = 1.5;
    /** Any movement at all counts as intent. */
    const INTENT = 0;
    /** Fallback release, for browsers with no `scrollend`. */
    const RELEASE_MS = 900;

    let armed = true;
    /**
     * True while a snap is in flight. Without this the `scroll` listener below
     * re-arms on the animation's own first frames and the next event of the
     * same gesture fires a second jump.
     */
    let snapping = false;
    /** Where the snap in flight is heading, so arrival can release it early. */
    let destinationY = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let touchY: number | null = null;

    const reduced = () =>
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /** Top of the section we snap to, which is also the height of the film. */
    const stageHeight = () => {
      const target = document.getElementById(targetId);
      if (!target) return 0;
      return target.getBoundingClientRect().top + window.scrollY;
    };

    const rearm = () => {
      if (!snapping) {
        armed = true;
        return;
      }
      // Arrived: release immediately rather than waiting out the fallback
      // timer, so input is blocked for as little time as possible.
      if (Math.abs(window.scrollY - destinationY) <= 2) settle();
    };

    const settle = () => {
      clearTimeout(timer);
      snapping = false;
      armed = true;
    };

    /** Where this gesture should land, or null to leave the scroll alone. */
    const destination = (down: boolean) => {
      const h = stageHeight();
      if (!h) return null;
      const y = window.scrollY;
      if (down && y < h * ON_FILM) return h;
      if (!down && y >= h * ON_FILM && y < h * BELOW) return 0;
      return null;
    };

    const snap = (event: Event, to: number) => {
      armed = false;
      snapping = true;
      destinationY = to;
      event.preventDefault();

      window.scrollTo({ top: to, behavior: reduced() ? 'auto' : 'smooth' });

      clearTimeout(timer);
      timer = setTimeout(settle, RELEASE_MS);
    };

    const onWheel = (event: WheelEvent) => {
      // A snap in flight owns the scroll. Every wheel event arriving while it
      // animates is swallowed, not just the one that started it — otherwise
      // the browser scrolls natively underneath the animation and the two
      // fight for the same pixels, which is what a fast flick used to look
      // like: a stutter down the page instead of one clean move.
      if (snapping) {
        event.preventDefault();
        return;
      }
      if (!armed || Math.abs(event.deltaY) <= INTENT) return;
      const to = destination(event.deltaY > 0);
      if (to === null) return;
      snap(event, to);
    };

    const onTouchStart = (event: TouchEvent) => {
      touchY = event.touches[0]?.clientY ?? null;
    };

    const onTouchMove = (event: TouchEvent) => {
      if (snapping) {
        event.preventDefault();
        return;
      }
      if (!armed || touchY === null) return;
      const y = event.touches[0]?.clientY;
      if (y === undefined) return;
      // A finger moving up the glass scrolls the page down.
      const delta = touchY - y;
      if (Math.abs(delta) <= INTENT) return;
      const to = destination(delta > 0);
      if (to === null) return;
      snap(event, to);
    };

    // `passive: false` on both: a passive listener cannot preventDefault, and
    // without that the browser scrolls underneath the animation and the two
    // fight each other.
    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('scrollend', settle);
    window.addEventListener('scroll', rearm, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('scrollend', settle);
      window.removeEventListener('scroll', rearm);
    };
  }, [targetId]);

  return null;
};
